import React, {
    useCallback,
    useEffect,
    useState
} from 'react';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import useInput from '../../hooks/useInput';
import metadata from './Person.metadata.json5';
import messages from './Person.messages';
import styles from './Person.module.scss';
import { useTranslator } from '@jutro/locale';
import AdacDatePicker from '../AdacDatePicker/AdacDatePicker';

const areMobilesEqual = (value, secondValue) => value === secondValue;
const areEmailsEqual = (value, secondValue) => value.trim() !== '' && secondValue.trim() !== '' && value === secondValue;

const Person = (props) => {
    const {
        id,
        value: personVM,
        onChange: writeValue,
        onValidate,
        isDateOfBirthVisible,
        isOtherIncidentNoteVisible
    } = props;

    const {
        value: enteredMobile,
        secondValue: repeatedMobile,
        isValid: isRepeatedMobileValid,
        valueChangeHandler: mobileChangeHandler,
        secondValueChangeHandler: repeatedMobileChangeHandler,
    } = useInput(areMobilesEqual);
    const {
        value: enteredEmail,
        secondValue: repeatedEmail,
        isValid: isRepeatedEmailValid,
        valueChangeHandler: emailChangeHandler,
        secondValueChangeHandler: repeatedEmailChangeHandler,
    } = useInput(areEmailsEqual);


    const translator = useTranslator();

    const [birthDate, setBirthDate] = useState(null);
    const [firstNameClasses, setfirstNameClasses] = useState(styles.validInputWithLabel);
    const [lastNameClasses, setlastNameClasses] = useState(styles.validInputWithLabel);

    const [validFirstName, setValidFirstName] = useState(true);
    const [validLastName, setValidLastName] = useState(true)


    const {
        isComponentValid,
        registerComponentValidation,
        onValidate: setComponentValidation
    } = useValidation(id);

    useEffect(() => {
        if (personVM !== undefined) {
            registerComponentValidation(() => {
                return personVM.aspects.valid
                && personVM.aspects.subtreeValid
                && isRepeatedMobileValid
                && isRepeatedEmailValid;
            });
        }
    }, [isRepeatedEmailValid, isRepeatedMobileValid, personVM, registerComponentValidation]);

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidate]);

    useEffect(() => {
        const phone = _.get(personVM, 'cellNumber.value');
        const email = _.get(personVM, 'emailAddress1.value');
        if (phone) {
            mobileChangeHandler(phone);
            repeatedMobileChangeHandler(phone);
        }
        if (email) {
            emailChangeHandler(email);
            repeatedEmailChangeHandler(email);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFieldChange = useCallback((value, path) => {
        _.set(personVM, path, value);
        switch (path) {
            case 'cellNumber':
                mobileChangeHandler(value);
                break;
            case 'emailAddress1':
                emailChangeHandler(value);
                break;
            default:
                break;
        }
    }, [emailChangeHandler, mobileChangeHandler, personVM]);

    const getLabelClassForValidity = useCallback((value, validStatus) => {
        if (_.isEmpty(value)) {
            return styles.mobileInput;
        }
        if (!_.isEmpty(value) && !validStatus) {
            return styles.invalidInputWithLabel;
        }
        return styles.validInputWithLabel;
    }, []);

    const onBirthDateChange = useCallback((date) => {
        const chosenDate = date ? {
            year: date.getFullYear(),
            month: date.getMonth(),
            day: date.getDate()
        } : null;
        setBirthDate(date);
        handleFieldChange(chosenDate, 'dateOfBirth_Adac');
    }, [handleFieldChange]);

    const hasLeadingOrTrailingSpaces = (value) => value && value.trim() !== value;

    const overrideProps = {
        firstNameField: {
            label: translator(messages.firstName),
            hideLabel: _.isEmpty(_.get(personVM, 'firstName.value')),
            className: _.isEmpty(_.get(personVM, 'firstName.value')) ? undefined : firstNameClasses,
            validationMessages: !validFirstName ? [translator(messages.noLeadingTrailingSpaces)] : [] ,
            onBlur: () => {
                if (hasLeadingOrTrailingSpaces(_.get(personVM, 'firstName.value'))) {
                    setfirstNameClasses(styles.invalidInputWithLabel);
                    setValidFirstName(false)
                } else {
                    setValidFirstName(true)
                    setfirstNameClasses(styles.validInputWithLabel);
                }
            }
        },
        lastNameField: {
            label: translator(messages.lastName),
            hideLabel: _.isEmpty(_.get(personVM, 'lastName.value')),
            className: _.isEmpty(_.get(personVM, 'lastName.value')) ? undefined : lastNameClasses,
            validationMessages: !validLastName ? [translator(messages.noLeadingTrailingSpaces)] : [] ,
            onBlur: () => {
                if (hasLeadingOrTrailingSpaces(_.get(personVM, 'lastName.value'))) {
                    setlastNameClasses(styles.invalidInputWithLabel);
                    setValidLastName(false)
                } else {
                    setValidLastName(true)
                    setlastNameClasses(styles.validInputWithLabel);
                }
            }
        },
        dateOfBirthField: {
            visible: isDateOfBirthVisible
        },
        mobileNumberField: {
            value: enteredMobile,
            showIntlPhoneValidationMessages_Adac: false,
            label: translator(messages.mobileNumber),
            hideLabel: _.isEmpty(enteredMobile),
            className: getLabelClassForValidity(enteredMobile, _.get(personVM, 'cellNumber.aspects.valid')),
            placeholder: translator(messages.mobileNumber)
        },
        repeatMobileNumberField: {
            value: repeatedMobile,
            validationMessages: !isRepeatedMobileValid
                ? [translator(messages.differentMobileNumber)] : [],
            showIntlPhoneValidationMessages_Adac: false,
            disabled: !_.get(personVM, 'cellNumber.aspects.valid'),
            label: translator(messages.repeatMobileNumber),
            hideLabel: _.isEmpty(repeatedMobile),
            className: getLabelClassForValidity(repeatedMobile, isRepeatedMobileValid),
            placeholder: translator(messages.repeatMobileNumber)
        },
        emailField: {
            value: enteredEmail,
            label: translator(messages.email),
            hideLabel: _.isEmpty(enteredEmail),
            className: getLabelClassForValidity(enteredEmail, _.get(personVM, 'emailAddress1.aspects.valid')),
        },
        repeatEmailField: {
            value: repeatedEmail,
            label: translator(messages.repeatEmail),
            hideLabel: _.isEmpty(repeatedEmail),
            className: getLabelClassForValidity(repeatedEmail, isRepeatedEmailValid),
            validationMessages: !isRepeatedEmailValid
                ? [translator(messages.differentEmailAddress)] : []
        },
        otherIncidentNote: {
            visible: isOtherIncidentNoteVisible,
            label: translator(messages.remark),
            hideLabel: _.isEmpty(_.get(personVM, 'otherIncidentNote.value')),
            className: _.isEmpty(_.get(personVM, 'otherIncidentNote.value')) ? undefined : styles.inputWithLabel
        },
        dateOfBirth: {
            visible: isDateOfBirthVisible,
            content: (
                <AdacDatePicker
                    date={birthDate}
                    isValid={_.get(personVM, 'dateOfBirth_Adac.aspects.subtreeValid')}
                    setDate={onBirthDateChange}
                    placeholder={translator(messages.dateOfBirth)}
                />
            )
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            handleFieldChange,
            repeatedMobileChangeHandler,
            repeatedEmailChangeHandler,
            writeValue
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={personVM}
            overrideProps={overrideProps}
            onModelChange={writeValue}
            callbackMap={resolvers.resolveCallbackMap}
            onValidationChange={setComponentValidation}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
};

Person.defaultProps = {
    isDateOfBirthVisible: false
};

export default Person;
